import React, { SetStateAction, useRef, useState } from "react";
import { ButtonBase, TextInputBase, PickerSelectSimple } from "@reusejs/react";
import { XIcon } from "@heroicons/react/solid";
import { AlertCircle, Loader2, Lock, Mail, Smartphone } from "lucide-react";
interface OtpModal {
  otp: string;
  setModal: React.Dispatch<SetStateAction<boolean>>;
  setOtp: React.Dispatch<SetStateAction<string>>;
  callback: (e: any) => void;
}

const OtpInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, inputValue: string) => {
    const newValue = value.split("");
    newValue[index] = inputValue;
    onChange(newValue.join(""));

    if (inputValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="flex justify-between">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          maxLength={1}
          value={value[index] || ""}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          className="w-12 h-12 text-center text-2xl border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-blue-500"
        />
      ))}
    </div>
  );
};

const Button = ({
  children,
  disabled,
  variant = "primary",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: React.ReactNode;
  variant?: "primary" | "secondary";
}) => (
  <button
    className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
      disabled
        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
        : variant === "primary"
        ? "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
        : "bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400"
    }`}
    disabled={disabled}
    {...props}
  >
    {disabled && variant === "primary" ? (
      <Loader2 className="inline-block w-5 h-5 mr-2 animate-spin" />
    ) : null}
    {children}
  </button>
);

const Alert = ({
  children,
  type = "info",
}: {
  children: React.ReactNode;
  type?: "info" | "error";
}) => (
  <div
    className={`flex items-center p-4 mb-4 text-sm rounded-lg ${
      type === "error" ? "text-red-800 bg-red-100" : "text-blue-800 bg-blue-100"
    }`}
  >
    <AlertCircle className="flex-shrink-0 inline w-5 h-5 mr-3" />
    <div>{children}</div>
  </div>
);

const OtpModal: React.FC<OtpModal> = ({ setModal, otp, setOtp, callback }) => {
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError("Please enter a 6-digit OTP.");
      return;
    }

    callback(otp);
    setModal(false);
  };

  return (
    <div>
      {/* Old Modal */}
      {/* <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50">
        <div className="relative bg-white shadow-lg rounded-lg max-w-lg w-full">
          <h1 className="text-xl font-medium rounded-lg not-italic leading-6 text-gray-500 text-center w-full p-4 bg-blue-100 border-b border-gray-500">
            Enter OTP
          </h1>
          <div className="absolute top-4 right-4">
            <div
              className="cursor-pointer bg-gray-50 rounded-full p-1 border text-gray-900"
              onClick={() => setModal(false)}
            >
              <XIcon className="h-4 w-4" aria-hidden="true" />
            </div>
          </div>
          <div className="p-4">
            <TextInputBase
              autoComplete="off"
              name={"otp"}
              onChange={(e: any) => {
                setOtp(e);
              }}
              placeholder={"Enter your OTP here..."}
              type={"text"}
              htmlFor={"otp"}
            />
            <div className="flex justify-center space-x-3 mt-4">
              <ButtonBase
                buttonBaseClasses={{ backgroundColor: "" }}
                type="submit"
                label={"Submit"}
                onClick={() => {
                  callback(otp);
                  setModal(false);
                }}
              />
            </div>
          </div>
        </div>
      </div> */}

      {/* second modal */}
      <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50 rounded-2xl">
        <div className="flex flex-col items-center justify-center bg-gray-100 rounded-2xl">
          <div className="w-full max-w-md p-8 space-y-8 bg-white  shadow-lg rounded-2xl">
            <div className="text-center">
              <div className="inline-block p-3 rounded-full bg-blue-100">
                <Lock className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="mt-4 text-2xl font-extrabold text-gray-900">
                Mobile No. Verification
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Verify your mobile number to proceed
              </p>
            </div>

            <form className="mt-8 space-y-6">
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  OTP sent to your Mobile number:
                </p>
              </div>
              <div>
                <label
                  htmlFor="otp"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Enter OTP
                </label>
                <OtpInput value={otp} onChange={setOtp} />
              </div>
              {error && <Alert type="error">{error}</Alert>}
              <Button
                type="submit"
                disabled={isValidating}
                onClick={handleSubmit}
              >
                {isValidating ? "Validating..." : "Verify OTP"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpModal;
